/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Plugin-declared permissions (AGL-435, Strapi RBAC-actions parity): a
 * plugin contributes NAMED permission keys with per-role-tier defaults.
 * `resolveRolePermissions` spreads them into every resolved set (custom
 * roles override key-by-key exactly like built-in keys), so
 * `ConsolePluginPageProps.permissions` and API-side resolution carry them
 * with zero app changes. Register at module scope from both the client
 * barrel and the /server entry (the AGL-428 pattern).
 */

export interface PluginPermission {
  /** Stable key ('managePos') — persisted in custom role docs. */
  key: string
  pluginId: string
  label: string
  description?: string
  /** Verdict per built-in role tier when a role doesn't name the key. */
  defaults: { admin: boolean; editor: boolean; viewer: boolean }
}

/*
 * One registry per process, keyed on `globalThis` under `Symbol.for`, like
 * `plugin-services.ts`. Next compiles the boot file and the route handlers
 * apart, so each can hold its own copy of this module: with the map at module
 * scope, a plugin's keys registered by one copy were absent from the copy
 * `resolveOrgPermissions` read, an absent key reads as refused, and the
 * register's `managePos` gate answered 403 to the site's own owner.
 */
const REGISTRY_KEY = Symbol.for('@aglyn/aglyn:plugin-permissions')

interface PluginPermissionTables {
  permissions: Map<string, PluginPermission>
  listeners: Set<() => void>
  version: { value: number }
}

const globalScope = globalThis as typeof globalThis & {
  [REGISTRY_KEY]?: PluginPermissionTables
}

const tables: PluginPermissionTables =
  globalScope[REGISTRY_KEY] ??
  (globalScope[REGISTRY_KEY] = {
    permissions: new Map(),
    listeners: new Set(),
    version: { value: 0 },
  })
const pluginPermissions = tables.permissions

/*
 * Whoever resolved a permission map before a plugin registered holds a map
 * without that plugin's keys, and a key that is absent reads as refused
 * (AGL-3228). The console's permission provider resolved its map when the
 * member document arrived; on a cold load of a plugin's deep link the
 * plugin's chunk can register AFTER that, so the owner was refused a surface
 * they hold by default until the next full render. So registration is
 * observable, the way `subscribePluginEntitlements` makes the org catalog's
 * registrations observable, and the provider re-reads on every change.
 */
const listeners = tables.listeners

/** Calls `listener` after every registration; answers the unsubscribe. */
export function subscribePluginPermissions(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * A number that changes whenever the registry does — the snapshot for a
 * `useSyncExternalStore` over `subscribePluginPermissions`.
 */
export function pluginPermissionsVersion(): number {
  return tables.version.value
}

function notifyListeners(): void {
  tables.version.value += 1
  for (const listener of [...listeners]) listener()
}

/**
 * Idempotent per key FOR ITS OWNER — the declaring plugin may re-register
 * (hot reload, a second surface, a repeated init) and its entry is replaced.
 *
 * A key already declared by a DIFFERENT plugin is refused (AGL-2484). This
 * map keyed on `permission.key` alone, so a later registration could redeclare
 * someone else's key with its own tier defaults — and these defaults are not
 * cosmetic: `managePos` is enforced server-side in
 * `libs/plugins/commerce/src/lib/server/pos-order.ts`, so moving
 * `defaults.viewer` to `true` hands a viewer the register. Reaching this
 * needs `PLUGIN_REMOTE_SERVER=enabled` (off by default), realm trust and a
 * signature; the registry closing its own door is the cheap part of that
 * stack.
 *
 * Per KEY, not per batch: a bundle that collides on one key still gets the
 * keys that are genuinely its own.
 */
export function registerPluginPermissions(
  permissions: readonly PluginPermission[],
): void {
  for (const permission of permissions) {
    const incumbent = pluginPermissions.get(permission.key)
    if (incumbent !== undefined && incumbent.pluginId !== permission.pluginId) {
      console.error(
        `[plugins] refused permission key "${permission.key}" to ` +
          `"${permission.pluginId}": already declared by ` +
          `"${incumbent.pluginId}"`,
      )
      continue
    }
    pluginPermissions.set(permission.key, permission)
  }
  notifyListeners()
}

export function listPluginPermissions(): PluginPermission[] {
  return [...pluginPermissions.values()]
}

/**
 * The registered keys alone (AGL-2474). `resolveRolePermissions` needs the
 * grantable key SPACE, not the tier verdicts: it used to iterate
 * `ORG_ROLE_PERMISSION_KEYS` — the legacy six — when applying overrides and
 * custom roles, so a plugin-declared key materialised into the resolved map
 * and could then only ever hold its tier default. Read live rather than
 * snapshotted: registration happens at plugin module scope, which can run
 * after any module-level constant here has already been evaluated.
 */
export function listPluginPermissionKeys(): string[] {
  return [...pluginPermissions.keys()]
}

/** Tier defaults for every registered plugin permission. */
export function pluginPermissionDefaults(
  tier: 'admin' | 'editor' | 'viewer',
): Record<string, boolean> {
  const defaults: Record<string, boolean> = {}
  for (const permission of pluginPermissions.values()) {
    defaults[permission.key] = permission.defaults[tier]
  }
  return defaults
}
